import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRadio } from '../contexts/RadioContext';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/Header.css';

// Import icons
import { Search, Close, PlayArrow, LocationOn, MusicNote, SearchOff, Keyboard, Public, Album } from '@mui/icons-material';

const Header = ({ title, showSearch = true }) => {
  const { getText } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const { searchStations, playStation, stations } = useRadio();

  // Generate unique lists of countries and genres for autocomplete
  const [countries, setCountries] = useState([]);
  const [genres, setGenres] = useState([]);
  const [stationNames, setStationNames] = useState([]);

  // New state for expanded results
  const [expandedResults, setExpandedResults] = useState(false);

  // Extract countries and genres from stations data for autocomplete
  useEffect(() => {
    if (stations && stations.length > 0) {
      // Extract unique countries
      const uniqueCountries = [...new Set(
        stations
          .map(station => station.country)
          .filter(Boolean)
      )].sort();
      
      // Extract unique genres
      const uniqueGenres = [...new Set(
        stations
          .flatMap(station => station.genres || [])
          .filter(Boolean)
      )].sort();
      
      // Extract station names
      const names = stations
        .map(station => ({ name: station.name, id: station.id }))
        .filter(station => station.name);
        
      setCountries(uniqueCountries);
      setGenres(uniqueGenres);
      setStationNames(names);
    }
  }, [stations]);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Generate autocomplete suggestions
  const generateSuggestions = (query) => {
    if (!query || query.length < 2) return [];
    
    const lowerQuery = query.toLowerCase();
    const results = [];
    
    // Add station name suggestions (max 3)
    const stationSuggestions = stationNames
      .filter(station => station.name.toLowerCase().includes(lowerQuery))
      .slice(0, 3)
      .map(station => ({
        type: 'station',
        value: station.name,
        id: station.id,
        icon: <PlayArrow fontSize="small" />
      }));
    
    // Add country suggestions (max 2)
    const countrySuggestions = countries
      .filter(country => country.toLowerCase().includes(lowerQuery))
      .slice(0, 2)
      .map(country => ({
        type: 'country',
        value: country,
        icon: <Public fontSize="small" />
      }));
    
    // Add genre suggestions (max 2)
    const genreSuggestions = genres
      .filter(genre => genre.toLowerCase().includes(lowerQuery))
      .slice(0, 2)
      .map(genre => ({
        type: 'genre',
        value: genre,
        icon: <Album fontSize="small" />
      }));
    
    return [
      ...stationSuggestions,
      ...countrySuggestions,
      ...genreSuggestions
    ];
  };

  // Search as user types
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    // Clear any existing loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSuggestions([]);
      setIsSearching(false);
      setExpandedResults(false);
      return;
    }

    // Generate autocomplete suggestions immediately
    const newSuggestions = generateSuggestions(searchQuery);
    setSuggestions(newSuggestions);
    setSelectedSuggestionIndex(-1);
    
    // Set loading state
    setIsSearching(true);
    
    // Use ref to track the timeout
    searchTimeoutRef.current = setTimeout(() => {
      const doSearch = async () => {
        try {
          const results = await searchStations(searchQuery);
          
          // Prevent state updates if component unmounted
          if (!inputRef.current) return;
          
          // Update results 
          const resultsToShow = expandedResults ? results : results.slice(0, 5);
          setSearchResults(resultsToShow);
          
          // Set a minimum loading time to prevent flickering
          loadingTimeoutRef.current = setTimeout(() => {
            setIsSearching(false);
            loadingTimeoutRef.current = null;
          }, 300);
          
        } catch (error) {
          setIsSearching(false);
        }
      };
      
      doSearch();
      searchTimeoutRef.current = null;
    }, 300); // 300ms delay to avoid too many requests

  }, [searchQuery, searchStations, expandedResults]);

  // Add this effect to reset expandedResults when search query changes
  useEffect(() => {
    // Reset expanded results when query changes
    setExpandedResults(false);
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchQuery.trim()) {
      // Instead of navigating, just show all results
      setExpandedResults(true);
      setSuggestions([]);
    }
  };

  const handleResultClick = (station) => {
    // Clear any loading state
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    setIsSearching(false);
    playStation(station);
    setSuggestions([]);
    setExpandedResults(false);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'station') {
      // Find the station by ID and play it
      const station = stations.find(s => s.id === suggestion.id);
      if (station) {
        playStation(station);
        setSearchQuery('');
        setSuggestions([]);
        setExpandedResults(false);
      }
    } else {
      // For genres and countries, set as search term
      setSearchQuery(suggestion.value);
      setSuggestions([]);
      setExpandedResults(false);
    }
  };

  const toggleSearch = () => {
    const newSearchOpen = !searchOpen;
    setSearchOpen(newSearchOpen);
    
    if (!newSearchOpen) {
      // If closing search
      setSearchQuery('');
      setSuggestions([]);
      setIsSearching(false);
      setExpandedResults(false);
      
      // Clear any timeouts
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    } else {
      // If opening search, focus the input
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleInputKeyDown = (e) => {
    // Handle keyboard navigation for autocomplete
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
      } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
        e.preventDefault();
        handleSuggestionClick(suggestions[selectedSuggestionIndex]);
      } else if (e.key === 'Escape') {
        setSuggestions([]);
      }
    } else if (e.key === 'Escape') {
      // Close dropdown on Escape key
      setSuggestions([]);
    }
  };

  // Prevent event propagation when clicking dropdown
  const handleDropdownClick = (e) => {
    e.stopPropagation();
  };

  return (
    <header className="app-header">
      <h1 className="header-title">{title}</h1>
      
      {showSearch && (
        <div className={`search-container ${searchOpen ? 'active' : ''}`}>
          <form onSubmit={handleSearchSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={getText('searchStations')}
              className="search-input"
              onKeyDown={handleInputKeyDown}
            />
            <button type="button" className="search-toggle" onClick={toggleSearch}>
              {searchOpen ? <Close /> : <Search />}
            </button>
            {searchOpen && (
              <button type="submit" className="search-submit">
                {getText('search')}
              </button>
            )}
            
            {/* Autocomplete suggestions */}
            {searchOpen && suggestions.length > 0 && (
              <div 
                className="autocomplete-dropdown" 
                ref={dropdownRef}
                onClick={handleDropdownClick}
              >
                {suggestions.map((suggestion, index) => (
                  <div 
                    key={`${suggestion.type}-${suggestion.value}`}
                    className={`autocomplete-item ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                  >
                    <span className="suggestion-icon">{suggestion.icon}</span>
                    <span className="suggestion-text">{suggestion.value}</span>
                    <span className="suggestion-type">
                      {suggestion.type === 'station' ? getText('station') : 
                       suggestion.type === 'genre' ? getText('genre') : getText('country')}
                    </span>
                  </div>
                ))}
                <div className="keyboard-hint">
                  <Keyboard fontSize="small" />
                  <span>{getText('useArrowKeys')}</span>
                </div>
              </div>
            )}
            
            {/* Search results dropdown */}
            {searchOpen && searchQuery.trim() && !suggestions.length && (
              <div 
                className="search-dropdown" 
                ref={dropdownRef}
                onClick={handleDropdownClick}
              >
                {isSearching ? (
                  <div className="search-loading">
                    <div className="search-loader"></div>
                    <span>{getText('searchingStations')}</span>
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    {searchResults.map((station) => (
                      <div 
                        key={station.id} 
                        className="search-result-item"
                        onClick={() => handleResultClick(station)}
                      >
                        <div className="result-info">
                          <div className="result-name">{station.name}</div>
                          {(station.location || station.country || station.city) && (
                            <div className="result-location">
                              <LocationOn fontSize="small" />
                              {station.city ? `${station.city}, ${station.country}` : station.country || station.location}
                            </div>
                          )}
                          {station.genres && station.genres.length > 0 && (
                            <div className="result-genres">
                              <MusicNote fontSize="small" />
                              {station.genres.join(', ')}
                            </div>
                          )}
                        </div>
                        <button 
                          className="play-result-btn" 
                          title={getText('playStation')}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResultClick(station);
                          }}
                        >
                          <PlayArrow />
                        </button>
                      </div>
                    ))}
                    {!expandedResults && searchResults.length === 5 && (
                      <div className="search-all-results" onClick={handleSearchSubmit}>
                        {getText('showMoreResults')}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="search-no-results">
                    <SearchOff />
                    <span>{getText('noStationsFound')}</span>
                    <p>{getText('tryDifferentSearch')}</p>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      )}
    </header>
  );
};

export default Header; 